import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import { getCollectionById, getCollections } from "./api"

const initialState = {
  byId: {}, // A map of collections by id
  byObservationId: {}, // a map of observation ID to a list of collections
  matchesByCtx: {
    // root context powering the collections list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // collection ids
      page: {}, // Pagination information
    },
    /*
      Example of other contexts
      "observation/123": {
        ids: [],
        page: {}, // Pagination information
      },
    */
  },
  /*
    example of _loadStatus pattern for a a single object
    byId: {
      '1': {
        id: '1',
        _loadStatus: 'idle',
      }
    }
 */
}

// Fetch collections that match the requested filters
export const fetchCollections = createAsyncThunk(
  "collections/fetch",
  async ({ filter, page, context }) => {
    const response = await getCollections({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

// Fetch collection by ID
export const fetchCollectionsById = createAsyncThunk(
  "collection/fetch",
  async (id) => {
    const response = await getCollectionById(id)
    return response
  }
)

export const collectionsSlice = createSlice({
  name: "collections",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    clearMatches: (state, action) => {
      state.matchesByCtx[action?.payload || "root"].ids = []
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchCollections.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchCollections.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"

        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"
          // Dont reset if we are paging?
          if (action.payload.page && action.payload.page.start_index == 0) {
            state.matchesByCtx[context].ids = []
          }
          action.payload.data &&
            action.payload.data.map((collection) => {
              // Add the observations to state by id (this can include ids not retrieved in this call)
              state.byId[collection.id] = collection
              // add the ids of the observations that match from this query
              state.matchesByCtx[context].ids.push(collection.id)
              state.matchesByCtx[context].page = action.payload.page
              // We map each recordingId to the collections its in
              if (collection.recordings) {
                collection.recordings.map((recId) => {
                  // create empty entry
                  if (!state.byObservationId[recId]) {
                    state.byObservationId[recId] = []
                  }
                  // Add oly if its unique (prevents duplicates on rerenders)
                  state.byObservationId[recId] = unique([
                    ...state.byObservationId[recId],
                    collection.id,
                  ])
                })
              }
            })
        }
      })
      .addCase(fetchCollections.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(fetchCollectionsById.pending, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "loading"
      })
      .addCase(fetchCollectionsById.fulfilled, (state, action) => {
        if (action.payload && action.payload.id) {
          state.byId[action.payload.id] = action.payload
          if (action.payload.recordings) {
            action.payload.recordings.map((recId) => {
              // create empty entry
              if (!state.byObservationId[recId]) {
                state.byObservationId[recId] = []
              }
              // Add oly if its unique (prevents duplicates on rerenders)
              state.byObservationId[recId] = unique([
                ...state.byObservationId[recId],
                action.payload.id,
              ])
            })
          }
          state.byId[action.payload.id]._loadStatus = "idle"
        }
      })
      .addCase(fetchCollectionsById.rejected, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "rejected"
        state.byId[action.meta.arg]._errorMessage = action.payload
      })
  },
})

/**
 * Util to get a unique list from a list
 * TODO discuss where to put these or if we should use something like ramda?
 * @param arr
 * @returns {unknown[]}
 */
const unique = (arr) => [...new Set(arr)]

export const selectCollectionsById = (state) => state.collections.byId
export const selectCollectionsByObservationId = (state) =>
  state.collections.byObservationId

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectCollectionsByContext(state, `rso/${id}`))
 */
export const selectCollectionsByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.collections.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`observations, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectCollectionsByIds(state, ["1", "2"]))
 */
export const selectCollectionsByIds = createSelector(
  [
    // first input - extract value from `state`
    selectCollectionsById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

export const { clearMatches } = collectionsSlice.actions

export default collectionsSlice.reducer
